import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import Select from 'react-select';
import { FaEraser } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import {
  simulacaoCalcular,
  simulacaoGetCedentes,
  simulacaoGetGruposEmpresa,
  simulacaoGetParametros,
} from '~/Services/Service';
import { extrairMensagemErro } from '~/Utils/Helpers';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import GridTitulos from './components/GridTitulos';
import HelperParcelas from './components/HelperParcelas';
import PainelResultados from './components/PainelResultados';

const DATA_REGEX = /^\d{2}\/\d{2}\/\d{4}$/;

const TIPOS_JURO = [
  { value: 'F', label: 'Final' },
  { value: 'D', label: 'Descontado' },
];

const TIPOS_DOCUMENTO = [
  { value: 0, label: 'Cheque' },
  { value: 1, label: 'Duplicata' },
  { value: 2, label: 'Promissória' },
  { value: 3, label: 'Boleto' },
];

const schemaCalcular = Yup.object().shape({
  dataBase: Yup.string()
    .required('Data base obrigatória')
    .matches(DATA_REGEX, 'Formato dd/MM/yyyy')
    .test('data-valida', 'Data inválida', value =>
      moment(value, 'DD/MM/YYYY', true).isValid()
    )
    .test('data-futura', 'Data base não pode ser futura', value => {
      const data = moment(value, 'DD/MM/YYYY', true);
      return !data.isValid() || data.isSameOrBefore(moment(), 'day');
    }),
  fatorOp: Yup.number()
    .required('Taxa obrigatória')
    .min(0, 'Taxa não pode ser negativa')
    .max(100, 'Taxa máxima 100%'),
  tipoJuro: Yup.string()
    .required('Tipo de juro obrigatório')
    .oneOf(['F', 'D'], 'Tipo de juro inválido'),
  tipoDocumento: Yup.number()
    .required('Tipo de documento obrigatório')
    .oneOf([0, 1, 2, 3], 'Tipo de documento inválido'),
  codigoGrupoEmpresa: Yup.number()
    .required('GE obrigatória')
    .positive('GE obrigatória')
    .integer('GE inválida'),
  codigoCedente: Yup.number()
    .required('Cedente obrigatório')
    .positive('Cedente obrigatório')
    .integer('Cedente inválido'),
});

const valoresIniciais = {
  dataBase: moment().format('DD/MM/YYYY'),
  fatorOp: '',
  tipoJuro: 'F',
  tipoDocumento: 0,
  codigoGrupoEmpresa: '',
  codigoCedente: '',
};

const normalizarResposta = res => (res && res.data ? res.data : res);

const montarPayload = (values, titulos) => ({
  data_base: values.dataBase,
  fator_op: Number(values.fatorOp || 0),
  tipo_juro: values.tipoJuro,
  tipo_documento: Number(values.tipoDocumento),
  codigo_grupo_empresa: Number(values.codigoGrupoEmpresa),
  codigo_cedente: Number(values.codigoCedente),
  titulos: titulos.map(titulo => ({
    index: Number(titulo.index),
    data_vencimento: titulo.dataVencimento,
    valor: Number(titulo.valor || 0),
    dias_adicionais: Number(titulo.diasAdicionais || 0),
  })),
});

const aplicarResultadoNosTitulos = (titulos, resultado) => {
  const calculados = resultado && resultado.titulos ? resultado.titulos : [];
  // Backend retorna Index = posição original no array (0-based); não ecoa o index do frontend.
  const calculadosPorPosicao = calculados.reduce((acc, titulo) => {
    acc[titulo.index] = titulo;
    return acc;
  }, {});

  return titulos.map((titulo, idx) => {
    const calculado = calculadosPorPosicao[idx];
    return {
      ...titulo,
      nDias: calculado ? calculado.n_dias : 0,
      liquido: calculado ? calculado.liquido : 0,
    };
  });
};

const possuiDadosParaCalculo = (values, titulos) =>
  values.codigoGrupoEmpresa &&
  values.codigoCedente &&
  values.fatorOp !== '' &&
  titulos.length > 0;

const ConteudoSimulacao = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  resetForm,
  grupos,
  cedentes,
  setCedentes,
  titulos,
  setTitulos,
  resultado,
  setResultado,
  perdaoFloating,
  setPerdaoFloating,
}) => {
  const [carregandoCedentes, setCarregandoCedentes] = useState(false);
  const [calculando, setCalculando] = useState(false);
  const [erro, setErro] = useState('');

  const gruposOptions = useMemo(
    () =>
      grupos.map(grupo => ({
        value: grupo.codigo_grupo_empresa,
        label: grupo.apelido,
      })),
    [grupos]
  );

  const cedentesOptions = useMemo(
    () =>
      cedentes.map(cedente => ({
        value: cedente.codigo_conta,
        label: cedente.apelido_conta,
        perdaoFloating: cedente.perdao_floating,
      })),
    [cedentes]
  );

  const titulosComResultado = useMemo(
    () => aplicarResultadoNosTitulos(titulos, resultado),
    [titulos, resultado]
  );

  useEffect(() => {
    let ativo = true;

    const carregarCedentes = async () => {
      setCedentes([]);
      setPerdaoFloating(false);
      setFieldValue('codigoCedente', '');

      if (!values.codigoGrupoEmpresa) return;

      setCarregandoCedentes(true);
      try {
        const res = await simulacaoGetCedentes(values.codigoGrupoEmpresa);
        if (!ativo) return;
        setCedentes(normalizarResposta(res) || []);
      } catch (err) {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      } finally {
        if (ativo) setCarregandoCedentes(false);
      }
    };

    carregarCedentes();

    return () => {
      ativo = false;
    };
  }, [values.codigoGrupoEmpresa]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cedente = cedentes.find(
      item => Number(item.codigo_conta) === Number(values.codigoCedente)
    );
    setPerdaoFloating(cedente ? cedente.perdao_floating === 'N' : false);
  }, [cedentes, values.codigoCedente, setPerdaoFloating]);

  useEffect(() => {
    let ativo = true;
    const timer = setTimeout(async () => {
      if (!possuiDadosParaCalculo(values, titulos)) {
        setResultado(null);
        setErro('');
        return;
      }

      try {
        await schemaCalcular.validate(values);
      } catch (validationError) {
        setErro(validationError.message);
        setResultado(null);
        return;
      }

      setCalculando(true);
      setErro('');
      try {
        const res = await simulacaoCalcular(montarPayload(values, titulos));
        if (!ativo) return;
        setResultado(normalizarResposta(res));
      } catch (err) {
        if (!ativo) return;
        const mensagem = extrairMensagemErro(err);
        setErro(mensagem);
        toast.error(mensagem);
      } finally {
        if (ativo) setCalculando(false);
      }
    }, 400);

    return () => {
      ativo = false;
      clearTimeout(timer);
    };
  }, [values, titulos, setResultado]);

  const limpar = () => {
    resetForm();
    setCedentes([]);
    setTitulos([]);
    setResultado(null);
    setPerdaoFloating(false);
    setErro('');
  };

  return (
    <>
      <div className="bg-white border rounded p-3 mb-3">
        <Row>
          <Col xs={12} md={4} lg={3}>
            <Form.Group>
              <Form.Label>Grupo de Empresa</Form.Label>
              <Select
                name="codigoGrupoEmpresa"
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={gruposOptions}
                value={
                  gruposOptions.find(
                    opcao => opcao.value === values.codigoGrupoEmpresa
                  ) || null
                }
                onChange={opcao =>
                  setFieldValue(
                    'codigoGrupoEmpresa',
                    opcao ? opcao.value : ''
                  )
                }
                placeholder="Selecione"
              />
              {touched.codigoGrupoEmpresa && errors.codigoGrupoEmpresa && (
                <div className="text-danger small mt-1">
                  {errors.codigoGrupoEmpresa}
                </div>
              )}
            </Form.Group>
          </Col>

          <Col xs={12} md={4} lg={3}>
            <Form.Group>
              <Form.Label>Cedente</Form.Label>
              <Select
                name="codigoCedente"
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={cedentesOptions}
                isDisabled={!values.codigoGrupoEmpresa || carregandoCedentes}
                value={
                  cedentesOptions.find(
                    opcao => opcao.value === values.codigoCedente
                  ) || null
                }
                onChange={opcao =>
                  setFieldValue('codigoCedente', opcao ? opcao.value : '')
                }
                placeholder={carregandoCedentes ? 'Carregando...' : 'Selecione'}
              />
              {touched.codigoCedente && errors.codigoCedente && (
                <div className="text-danger small mt-1">
                  {errors.codigoCedente}
                </div>
              )}
            </Form.Group>
          </Col>

          <Col xs={12} sm={6} md={4} lg={2}>
            <Form.Group>
              <Form.Label>Data base</Form.Label>
              <Form.Control
                type="text"
                name="dataBase"
                placeholder="dd/MM/aaaa"
                value={values.dataBase}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.dataBase && !!errors.dataBase}
              />
              <Form.Control.Feedback type="invalid">
                {errors.dataBase}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col xs={12} sm={6} md={4} lg={2}>
            <Form.Group>
              <Form.Label>Taxa operação (%)</Form.Label>
              <Form.Control
                type="number"
                name="fatorOp"
                min="0"
                max="100"
                step="0.0001"
                value={values.fatorOp}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.fatorOp && !!errors.fatorOp}
              />
              <Form.Control.Feedback type="invalid">
                {errors.fatorOp}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col xs={12} sm={6} md={4} lg={2}>
            <Form.Group>
              <Form.Label>Tipo de juro</Form.Label>
              <Select
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={TIPOS_JURO}
                value={TIPOS_JURO.find(opcao => opcao.value === values.tipoJuro)}
                onChange={opcao =>
                  setFieldValue('tipoJuro', opcao ? opcao.value : 'F')
                }
                isClearable={false}
              />
            </Form.Group>
          </Col>

          <Col xs={12} sm={6} md={4} lg={2}>
            <Form.Group>
              <Form.Label>Tipo documento</Form.Label>
              <Select
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={TIPOS_DOCUMENTO}
                value={TIPOS_DOCUMENTO.find(
                  opcao => opcao.value === values.tipoDocumento
                )}
                onChange={opcao =>
                  setFieldValue('tipoDocumento', opcao ? opcao.value : 0)
                }
                isClearable={false}
              />
            </Form.Group>
          </Col>

          <Col xs={12} lg={2} className="d-flex align-items-end">
            <Button variant="outline-secondary" onClick={limpar} className="mb-3">
              <FaEraser className="mr-1" />
              Limpar
            </Button>
          </Col>
        </Row>

        {calculando && (
          <div className="small text-muted">
            <Spinner animation="border" size="sm" className="mr-2" />
            Calculando simulação...
          </div>
        )}
        {erro && <Alert variant="warning" className="mb-0 mt-2">{erro}</Alert>}
      </div>

      <Row>
        <Col xs={12} lg={3} className="mb-3">
          <HelperParcelas onPreencher={setTitulos} />
        </Col>
        <Col xs={12} lg={6} className="mb-3">
          <GridTitulos
            titulos={titulosComResultado}
            onTitulosChange={setTitulos}
            isDPlusEditavel={perdaoFloating}
          />
        </Col>
        <Col xs={12} lg={3} className="mb-3">
          <PainelResultados resultado={resultado} />
        </Col>
      </Row>
    </>
  );
};

const SimulacaoProposta = () => {
  const [grupos, setGrupos] = useState([]);
  const [cedentes, setCedentes] = useState([]);
  const [titulos, setTitulos] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [perdaoFloating, setPerdaoFloating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ativo = true;

    const carregarDadosIniciais = async () => {
      try {
        const [gruposRes] = await Promise.all([
          simulacaoGetGruposEmpresa(),
          simulacaoGetParametros(),
        ]);
        if (!ativo) return;
        setGrupos(normalizarResposta(gruposRes) || []);
      } catch (err) {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      } finally {
        if (ativo) setLoading(false);
      }
    };

    carregarDadosIniciais();

    return () => {
      ativo = false;
    };
  }, []);

  return (
    <div>
      <Header title="Simulação de Propostas" />
      {loading ? (
        <Loading />
      ) : (
        <div className="container-fluid py-3">
          <Formik
            initialValues={valoresIniciais}
            validationSchema={schemaCalcular}
            onSubmit={() => {}}
          >
            {formikProps => (
              <ConteudoSimulacao
                {...formikProps}
                grupos={grupos}
                cedentes={cedentes}
                setCedentes={setCedentes}
                titulos={titulos}
                setTitulos={setTitulos}
                resultado={resultado}
                setResultado={setResultado}
                perdaoFloating={perdaoFloating}
                setPerdaoFloating={setPerdaoFloating}
              />
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default SimulacaoProposta;
